/**
 * DOCUMENTACIÓN APLICABLE EN OT/LÍNEA — SÓLO LECTURA (FASE 4.4-F)
 *
 * Un único componente, parametrizado por `ruta` (igual que `Evidencias`,
 * 4.4-D): quien lo integra decide el contexto (OT o línea) pasando el
 * endpoint ya resuelto. Nunca sube ni gestiona nada — el catálogo se
 * gestiona en `/documentos` y la asociación a Plan en la pestaña
 * «Documentos» del editor de Plan; aquí sólo se lee el snapshot que el
 * generador ya congeló (`ot_documentos`, 0044). Abrir/descargar usa el
 * mismo patrón de blob autenticado que `Evidencias`.
 */
import { useState, type ReactNode } from 'react';

import { api, descargarConSesion, ETIQUETAS_TIPO_DOCUMENTO, type DocumentoAplicadoOt } from '../api.ts';
import { useSesion } from '../contexto.tsx';
import { Cargando, Error as ErrorCaja, fechaHora, useDatos, Vacio } from './base.tsx';

function truncar(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

function formatoBytes(n: number): string {
  if (n < 1024) return `${String(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function abrirDocumento(id: string): Promise<Blob | null> {
  return descargarConSesion(`/api/ot-documentos/${id}/archivo`);
}

function BotonAbrir({ id, nombreOriginal }: { id: string; nombreOriginal: string }): ReactNode {
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);

  async function abrir(): Promise<void> {
    setCargando(true);
    setFallo(false);
    const blob = await abrirDocumento(id);
    setCargando(false);
    if (blob === null) {
      setFallo(true);
      return;
    }
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <span className="fila" style={{ gap: 6 }}>
      <button type="button" className="pequeno" disabled={cargando} onClick={() => void abrir()} aria-label={`Abrir ${nombreOriginal}`}>
        {cargando ? 'Abriendo…' : 'Abrir'}
      </button>
      {fallo && <span className="pequena tenue" role="alert">No se ha podido abrir.</span>}
    </span>
  );
}

export interface DocumentosAplicablesProps {
  readonly ruta: string;
  readonly titulo?: string;
}

export function DocumentosAplicables({ ruta, titulo = 'Documentación aplicable' }: DocumentosAplicablesProps): ReactNode {
  const { puede } = useSesion();
  const puedeLeer = puede('documentos:leer');

  const estado = useDatos(
    () => (puedeLeer ? api.get<{ filas: DocumentoAplicadoOt[] }>(ruta) : Promise.resolve({ filas: [] })),
    [ruta, puedeLeer],
  );

  if (!puedeLeer) return null;

  return (
    <div className="panel documentos-aplicables-seccion">
      <div className="panel-cabecera">
        <h3>{titulo}</h3>
      </div>
      <div className="panel-cuerpo">
        {estado.cargando && estado.datos === null && <Cargando texto="Cargando documentación…" />}
        {estado.error !== null && <ErrorCaja error={estado.error} reintentar={estado.recargar} />}
        {estado.datos !== null &&
          (estado.datos.filas.length === 0 ? (
            <Vacio titulo="No hay documentación aplicable registrada" />
          ) : (
            <ul className="lista-documentos-aplicables">
              {estado.datos.filas.map((f) => (
                <li key={f.id} className="documento-aplicable-tarjeta">
                  <div className="documento-aplicable-datos">
                    <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
                      <span className="etiqueta info">{ETIQUETAS_TIPO_DOCUMENTO[f.tipoDocumento] ?? f.tipoDocumento}</span>
                      <span className="mono pequena">{f.codigo}</span>
                      <span className="pequena tenue">v{f.version}</span>
                    </div>
                    <div className="documento-aplicable-titulo" title={f.titulo}>
                      {truncar(f.titulo, 60)}
                    </div>
                    <div className="pequena tenue">
                      {truncar(f.nombreOriginal, 40)} · {formatoBytes(f.tamanoBytes)} · aplicado {fechaHora(f.resueltoEn)}
                    </div>
                  </div>
                  <div className="documento-aplicable-acciones">
                    <BotonAbrir id={f.id} nombreOriginal={f.nombreOriginal} />
                  </div>
                </li>
              ))}
            </ul>
          ))}
      </div>
    </div>
  );
}
